import { Button, ChipModal, ChipModalBody, ChipModalFooter, ChipModalHeader, Input, Label } from "@ondit/ui";
import { useId, useState } from "react";
import { useAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { useI18n, useT } from "~/core/i18n";

export function ResultLink({
	title,
	value,
	onSave,
	disabled = false,
	compact = false,
	open: controlledOpen,
	onOpenChange,
	hideTrigger = false,
}: {
	title: string;
	value?: string | null;
	onSave: (url: string) => Promise<void>;
	disabled?: boolean;
	compact?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	hideTrigger?: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const id = useId();
	const [localOpen, setLocalOpen] = useState(false);
	const open = controlledOpen ?? localOpen;
	const setOpen = (value: boolean) => {
		setLocalOpen(value);
		onOpenChange?.(value);
	};
	const [url, setUrl] = useState(value ?? "");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	return (
		<>
			{!hideTrigger && (
				<Button
					variant="default"
					size={compact ? "sm" : undefined}
					disabled={disabled || !editor}
					onClick={() => {
						setUrl(value ?? "");
						setError("");
						setOpen(true);
					}}
				>
					{t("publishCalendar.result")}
				</Button>
			)}
			<ChipModal open={open} onOpenChange={setOpen} dismissDisabled={busy} srTitle={t("publishCalendar.result")}>
				<ChipModalHeader onClose={() => setOpen(false)} closeAriaLabel={t("common.close")}>
					{t("publishCalendar.result")}
				</ChipModalHeader>
				<ChipModalBody className="px-(--s-8)">
					<p>{title}</p>
					<p className="hint">{t("publishFlow.linkHelp")}</p>
					<form
						id={id}
						onSubmit={async (e) => {
							e.preventDefault();
							if (busy || !editor) return;
							setBusy(true);
							setError("");
							try {
								await onSave(url.trim());
								setOpen(false);
							} catch (e) {
								setError(e instanceof ApiError ? e.localized(locale) : String(e));
							} finally {
								setBusy(false);
							}
						}}
					>
						<fieldset className="bare-fieldset stack" disabled={!editor || busy}>
							<Label htmlFor={`${id}-url`}>{t("publishCalendar.linkUrl")}</Label>
							<Input
								id={`${id}-url`}
								required
								type="url"
								placeholder="https://"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
							/>
							{error && <p role="alert">{error}</p>}
						</fieldset>
					</form>
				</ChipModalBody>
				<ChipModalFooter
					onCancel={() => setOpen(false)}
					cancelLabel={t("common.cancel")}
					primaryAction={{
						label: t("common.save"),
						form: id,
						type: "submit",
						disabled: !editor || busy || !url.trim(),
					}}
				/>
			</ChipModal>
		</>
	);
}
